import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Bar, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { get, downloadFile, toastError } from "@/lib/api";
import { fmtDate, todayISO, addDaysISO, num } from "@/lib/format";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ErrorState } from "@/components/common/States";
import { FileDown, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Fact, OccupancyBar, dayLabel, accommodationUrl } from "./AccommodationBits";

/* ------------------------------------------------------------------ periods ---- */
const monthStart = (iso) => `${iso.slice(0, 7)}-01`;
const addMonths = (iso, n) => { const d = new Date(iso + "T00:00:00Z"); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 10); };
const PRESETS = [
  ["standard", "Siste 8 uker + 4 frem"],
  ["forrige_maned", "Forrige måned"],
  ["denne_maned", "Denne måneden"],
  ["kvartal", "Siste 3 måneder"],
  ["neste_3", "Neste 3 måneder"],
  ["ar", "Hittil i år"],
  ["egen", "Egen periode"],
];
const presetRange = (key, today) => {
  const ms = monthStart(today);
  switch (key) {
    case "forrige_maned": return { start: addMonths(ms, -1), end: ms, group: "week" };
    case "denne_maned": return { start: ms, end: addMonths(ms, 1), group: "week" };
    case "kvartal": return { start: addMonths(ms, -3), end: addDaysISO(today, 1), group: "month" };
    case "neste_3": return { start: today, end: addMonths(ms, 3), group: "week" };
    case "ar": return { start: `${today.slice(0, 4)}-01-01`, end: addDaysISO(today, 1), group: "month" };
    default: return { start: addDaysISO(today, -56), end: addDaysISO(today, 28), group: "week" };
  }
};

/* ------------------------------------------------------------------ chart ---- */
const C = { capacity: "#8a93a3", actual: "#14171f", planned: "#2670F2", untracked: "#c3c9d4" };

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const w = payload[0]?.payload || {};
  const rows = [["Kapasitet (sengedøgn)", w.capacity_nights], ["Faktiske", w.actual_nights], ["Planlagte", w.planned_nights], ["Belegg", `${w.occupancy_pct ?? 0} %`], ["Senger (snitt)", w.beds_avg], w.untracked_capacity_nights ? ["Kunde-driftet kapasitet", w.untracked_capacity_nights] : null].filter(Boolean);
  return (
    <div className="rounded-lg bg-card px-3 py-2 text-[12px] shadow-pop" data-testid="occupancy-chart-tooltip">
      <div className="mb-1 font-medium">{w.label} <span className="text-muted-foreground">· fra {dayLabel(w.start)}{w.kind === "future" ? " · prognose" : w.kind === "current" ? " · pågår" : ""}</span></div>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 text-muted-foreground tabular"><span>{k}</span><span className="font-mono text-foreground">{v ?? 0}</span></div>
      ))}
    </div>
  );
};

const OccupancyChart = ({ periods = [], height = 240, minWidth = 0 }) => {
  const showUntracked = periods.some((p) => p.untracked_capacity_nights > 0);
  const interval = periods.length > 30 ? 3 : periods.length > 16 ? 1 : 0;
  const nowIdx = periods.findIndex((p) => p.kind === "current");
  return (
    <div className={cn(minWidth > 0 && "overflow-x-auto scrollbar-thin")} data-testid="occupancy-chart">
      <div style={{ height, minWidth: minWidth || undefined }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={periods} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={3}>
            <XAxis dataKey="label" tick={{ fontSize: 10.5, fill: "#8a93a3" }} axisLine={false} tickLine={false} interval={interval} />
            <YAxis width={40} tick={{ fontSize: 10.5, fill: "#8a93a3" }} axisLine={false} tickLine={false} />
            <Tooltip content={<Tip />} cursor={{ fill: "rgba(16,24,40,0.04)" }} />
            {nowIdx >= 0 && <ReferenceLine x={periods[nowIdx].label} stroke={C.planned} strokeOpacity={0.35} strokeDasharray="3 3" label={{ value: "nå", position: "insideTopRight", fontSize: 10, fill: "#8a93a3" }} />}
            <Bar dataKey="actual_nights" name="Faktiske" stackId="n" fill={C.actual} fillOpacity={0.8} maxBarSize={28} isAnimationActive={false} />
            <Bar dataKey="planned_nights" name="Planlagte" stackId="n" fill={C.planned} fillOpacity={0.45} radius={[2, 2, 0, 0]} maxBarSize={28} isAnimationActive={false} />
            <Line type="stepAfter" dataKey="capacity_nights" name="Kapasitet" stroke={C.capacity} strokeWidth={1.25} dot={false} isAnimationActive={false} />
            {showUntracked && <Line type="stepAfter" dataKey="untracked_capacity_nights" name="Kunde-driftet" stroke={C.untracked} strokeDasharray="3 3" strokeWidth={1} dot={false} isAnimationActive={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ tables ---- */
const Th = ({ children, className, right }) => <th className={cn("py-2 pr-3 font-medium", right ? "text-right" : "text-left", className)}>{children}</th>;
const Td = ({ children, className, right, mono }) => <td className={cn("py-2 pr-3", right && "text-right", mono && "font-mono tabular", className)}>{children}</td>;
const KIND = { past: "", current: "bg-primary/[0.04]", future: "text-muted-foreground" };

const PeriodTable = ({ periods, group }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[13px]" data-testid="occupancy-period-table">
      <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
        <tr className="border-b"><Th>{group === "month" ? "Måned" : "Uke"}</Th><Th>Fra</Th><Th right>Senger</Th><Th right>Kapasitet</Th><Th right>Faktiske</Th><Th right>Planlagte</Th><Th className="w-[180px]">Belegg</Th></tr>
      </thead>
      <tbody>
        {periods.map((p) => (
          <tr key={p.key} className={cn("border-b border-border/70", KIND[p.kind])} data-testid={`occupancy-period-row-${p.key}`}>
            <Td className="font-medium text-foreground">{p.label}{p.kind === "current" && <span className="ml-1.5 text-[11px] font-normal text-primary">pågår</span>}{p.kind === "future" && <span className="ml-1.5 text-[11px] font-normal">prognose</span>}</Td>
            <Td className="text-muted-foreground">{dayLabel(p.start)}</Td>
            <Td right mono>{num(p.beds_avg)}</Td>
            <Td right mono>{num(p.capacity_nights)}</Td>
            <Td right mono>{num(p.actual_nights)}</Td>
            <Td right mono className="text-muted-foreground">{num(p.planned_nights)}</Td>
            <Td><div className="flex items-center gap-2"><OccupancyBar pct={p.occupancy_pct} tone={p.occupancy_pct >= 95 ? "danger" : p.occupancy_pct >= 85 ? "warning" : undefined} className="w-24" /><span className="font-mono text-[12px] tabular">{p.occupancy_pct} %</span></div></Td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CampTable = ({ rows, mode }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[13px]" data-testid={`occupancy-${mode}-table`}>
      <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
        <tr className="border-b">
          <Th>{mode === "camp" ? "Camp" : "Kunde"}</Th>
          {mode === "camp" ? <Th>Kunde · driftes av</Th> : <Th right>Camper</Th>}
          <Th right>Kapasitet</Th><Th right>Faktiske</Th><Th right>Planlagte</Th><Th className="w-[180px]">Belegg</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-border/70" data-testid={`occupancy-${mode}-row-${r.key}`}>
            <Td className="font-medium text-foreground">
              {mode === "camp" ? <Link to={accommodationUrl(r.key, "rapport")} className="hover:underline underline-offset-[3px]">{r.name}</Link> : r.name}
              {mode === "camp" && r.project_number && <span className="ml-1.5 font-mono text-[11px] font-normal text-muted-foreground">{r.project_number}</span>}
            </Td>
            {mode === "camp" ? <Td className="text-muted-foreground">{r.customer_name || "–"}{!r.tracked && <span className="ml-1.5">· {r.operator ? `${r.operator} (kunde)` : "kunde drifter"}</span>}</Td> : <Td right mono>{r.camps}</Td>}
            <Td right mono>{num(r.capacity_nights)}</Td>
            <Td right mono>{r.tracked ? num(r.actual_nights) : <span className="text-muted-foreground">ikke ført</span>}</Td>
            <Td right mono className="text-muted-foreground">{r.tracked ? num(r.planned_nights) : "–"}</Td>
            <Td>{r.tracked ? <div className="flex items-center gap-2"><OccupancyBar pct={r.occupancy_pct} tone={r.occupancy_pct >= 95 ? "danger" : r.occupancy_pct >= 85 ? "warning" : undefined} className="w-24" /><span className="font-mono text-[12px] tabular">{r.occupancy_pct} %</span></div> : <span className="text-[12px] text-muted-foreground">–</span>}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Beleggsrapport – sengedøgn per periode × camp (kapasitet / faktisk / planlagt). Billing basis and management view.
 * Portfolio mode (no projectId) rolls up per camp and per customer; project mode shows the camp period by period.
 * Everything is computed deterministically server-side and scoped to the projects the user may see.
 */
export const OccupancyReport = ({ projectId, projectNumber, className }) => {
  const wide = useMediaQuery("(min-width: 1024px)");
  const today = todayISO();
  const [preset, setPreset] = useState("standard");
  const [range, setRange] = useState(() => presetRange("standard", today));
  const [customerId, setCustomerId] = useState("");
  const [rollup, setRollup] = useState("camp");
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const portfolio = !projectId;
  const params = { start: range.start, end: range.end, group: range.group, project_id: projectId || undefined, customer_id: customerId || undefined };
  const valid = range.start && range.end && range.end > range.start;

  const q = useQuery({
    queryKey: ["ink", "occupancy", projectId || "portfolio", params],
    queryFn: () => get("/innkvartering/reports/occupancy", params),
    enabled: valid,
    placeholderData: keepPreviousData,
  });
  const rep = q.data;
  const t = rep?.totals;
  // customer filter options come from the unfiltered, server-scoped report itself (no global customer list needed)
  useEffect(() => {
    if (portfolio && !customerId && rep?.by_customer?.length) setCustomers(rep.by_customer.map((c) => ({ id: c.key, name: c.name })));
  }, [rep, portfolio, customerId]);

  const pickPreset = (k) => { setPreset(k); if (k !== "egen") setRange(presetRange(k, today)); };
  const setDate = (k, v) => { setPreset("egen"); setRange((r) => ({ ...r, [k]: v })); };
  const exportCsv = async () => {
    setBusy(true);
    try {
      const name = await downloadFile("/innkvartering/reports/occupancy", { ...params, format: "csv" }, `Belegg-${range.start}_${range.end}.csv`);
      toast.success("Rapport eksportert", { description: name });
    } catch (e) { toastError(e, "Kunne ikke eksportere rapporten"); } finally { setBusy(false); }
  };

  const occTone = (p) => (p >= 95 ? "danger" : p >= 85 ? "warning" : undefined);
  const nights = useMemo(() => (rep?.periods || []).reduce((a, p) => a + p.days, 0), [rep]);

  return (
    <div className={cn("space-y-5", className)} data-testid="occupancy-report">
      {/* toolbar */}
      <div className="flex flex-wrap items-end gap-3" data-testid="occupancy-toolbar">
        <div className="space-y-1.5">
          <Label className="text-[11.5px] text-muted-foreground">Periode</Label>
          <Select value={preset} onValueChange={pickPreset}>
            <SelectTrigger className="h-8 w-[190px] text-[12.5px]" data-testid="occupancy-preset-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PRESETS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`occupancy-preset-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="occ-start" className="text-[11.5px] text-muted-foreground">Fra</Label>
          <Input id="occ-start" type="date" value={range.start} onChange={(e) => setDate("start", e.target.value)} className="h-8 w-[150px] text-[12.5px]" data-testid="occupancy-start-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="occ-end" className="text-[11.5px] text-muted-foreground">Til</Label>
          <Input id="occ-end" type="date" value={range.end} min={range.start} onChange={(e) => setDate("end", e.target.value)} className="h-8 w-[150px] text-[12.5px]" data-testid="occupancy-end-input" />
        </div>
        <ToggleGroup type="single" value={range.group} onValueChange={(v) => v && setRange((r) => ({ ...r, group: v }))} className="h-8 rounded-md border p-0.5" data-testid="occupancy-group-toggle">
          <ToggleGroupItem value="week" className="h-7 px-2.5 text-[12px]" data-testid="occupancy-group-week">Uke</ToggleGroupItem>
          <ToggleGroupItem value="month" className="h-7 px-2.5 text-[12px]" data-testid="occupancy-group-month">Måned</ToggleGroupItem>
        </ToggleGroup>
        {portfolio && customers.length > 1 && (
          <Select value={customerId || "alle"} onValueChange={(v) => setCustomerId(v === "alle" ? "" : v)}>
            <SelectTrigger className="h-8 w-[180px] text-[12.5px]" data-testid="occupancy-customer-select"><SelectValue placeholder="Alle kunder" /></SelectTrigger>
            <SelectContent><SelectItem value="alle">Alle kunder</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8" onClick={exportCsv} disabled={busy || !rep} data-testid="occupancy-export-csv-button">{busy ? <Loader2 className="animate-spin" /> : <FileDown />} Eksporter CSV</Button>
        </div>
      </div>

      {!valid ? (
        <p className="text-[12.5px] text-muted-foreground" data-testid="occupancy-invalid-range">Sluttdato må være etter startdato.</p>
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !rep ? (
        <div className="space-y-4" data-testid="occupancy-loading"><div className="grid grid-cols-3 gap-4 sm:grid-cols-6">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div><Skeleton className="h-60" /><Skeleton className="h-40" /></div>
      ) : !rep.periods.length ? (
        <div className="rounded-lg border border-dashed px-6 py-12 text-center" data-testid="occupancy-empty">
          <p className="text-[14px] font-medium">Ingen rom i perioden</p>
          <p className="mt-1 text-[12.5px] text-muted-foreground">Kapasitet oppstår når boligmoduler er plassert på {portfolio ? "en camp" : "campen"} i perioden.</p>
        </div>
      ) : (
        <>
          {/* facts */}
          <div className={cn("grid grid-cols-3 gap-x-5 gap-y-4", portfolio ? "sm:grid-cols-6" : "sm:grid-cols-5")} data-testid="occupancy-facts">
            <Fact label="Belegg" value={`${t.occupancy_pct} %`} sub={t.actual_occupancy_pct != null ? `${t.actual_occupancy_pct} % faktisk hittil` : "kun prognose"} tone={occTone(t.occupancy_pct)} testId="occupancy-fact-occupancy" />
            <Fact label="Kapasitet" value={num(t.capacity_nights)} sub={`sengedøgn · ${nights} netter`} testId="occupancy-fact-capacity" />
            <Fact label="Faktiske" value={num(t.actual_nights)} sub="sengedøgn hittil" testId="occupancy-fact-actual" />
            <Fact label="Planlagte" value={num(t.planned_nights)} sub="sengedøgn fremover" testId="occupancy-fact-planned" />
            {portfolio ? (
              <>
                <Fact label="Camper" value={`${t.camps_tracked}/${t.camps}`} sub="Stord fører belegg" testId="occupancy-fact-camps" />
                <Fact label="Kunder" value={num(t.customers)} sub={t.untracked_capacity_nights ? `${num(t.untracked_capacity_nights)} sengedøgn kunde-driftet` : "alle ført av Stord"} testId="occupancy-fact-customers" />
              </>
            ) : (
              <Fact label="Senger" value={num(rep.periods[rep.periods.length - 1]?.beds_avg)} sub="snitt siste periode" testId="occupancy-fact-beds" />
            )}
          </div>

          {/* chart */}
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground" data-testid="occupancy-legend">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-foreground/80" aria-hidden /> Faktiske sengedøgn</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-primary/45" aria-hidden /> Planlagte</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-[1.5px] w-3 bg-[#8a93a3]" aria-hidden /> Kapasitet</span>
              {t.untracked_capacity_nights > 0 && <span className="inline-flex items-center gap-1.5"><span className="h-[1px] w-3 border-t border-dashed border-[#c3c9d4]" aria-hidden /> Kunde-driftet kapasitet</span>}
              <span className="ml-auto">{rep.group === "month" ? "per måned" : "per ISO-uke"} · {dayLabel(rep.start)} – {dayLabel(addDaysISO(rep.end, -1))}</span>
            </div>
            <OccupancyChart periods={rep.periods} height={wide ? 240 : 200} minWidth={wide ? 0 : Math.max(520, rep.periods.length * 34)} />
          </div>

          {/* tables */}
          {portfolio ? (
            <section className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-[13.5px] font-semibold tracking-tight">Fordeling</h3>
                <ToggleGroup type="single" value={rollup} onValueChange={(v) => v && setRollup(v)} className="h-8 rounded-md border p-0.5" data-testid="occupancy-rollup-toggle">
                  <ToggleGroupItem value="camp" className="h-7 px-2.5 text-[12px]" data-testid="occupancy-rollup-camp">Per camp</ToggleGroupItem>
                  <ToggleGroupItem value="customer" className="h-7 px-2.5 text-[12px]" data-testid="occupancy-rollup-customer">Per kunde</ToggleGroupItem>
                  <ToggleGroupItem value="period" className="h-7 px-2.5 text-[12px]" data-testid="occupancy-rollup-period">Per periode</ToggleGroupItem>
                </ToggleGroup>
              </div>
              {rollup === "camp" && <CampTable rows={rep.by_camp} mode="camp" />}
              {rollup === "customer" && <CampTable rows={rep.by_customer} mode="customer" />}
              {rollup === "period" && <PeriodTable periods={rep.periods} group={rep.group} />}
            </section>
          ) : (
            <section className="space-y-3">
              <h3 className="text-[13.5px] font-semibold tracking-tight">{projectNumber ? `Camp ${projectNumber}` : "Campen"} · {rep.group === "month" ? "per måned" : "per uke"}</h3>
              <PeriodTable periods={rep.periods} group={rep.group} />
            </section>
          )}

          <p className="flex items-start gap-2 text-[11.5px] leading-4 text-muted-foreground" data-testid="occupancy-footnote">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>Faktiske sengedøgn = netter til og med i går med innsjekket person. Planlagte = aktive opphold fra i dag og fremover. Kapasitet = plasserte senger × netter. Camper kunden drifter selv har kapasitet, men oppholdene føres ikke i Stord OS («ikke ført»). Rapporten er beregnet {fmtDate(rep.today, "d. MMM yyyy")} og kan brukes som fakturagrunnlag.</span>
          </p>
        </>
      )}
    </div>
  );
};
